import React from 'react'
import {
  Container,
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import CrossOrange from '../../images/crossmark-orange.inline.svg'
import CrossRed from '../../images/crossmark-red.inline.svg'
import TickGreen from '../../images/checkmarkgreen.inline.svg'
import TickGreenCicle from '../../images/checkmarkgreen-circle.inline.svg'
import TickOrangeCicle from '../../images/checkmarkorange-circle.inline.svg'
import ExclamationRedCicle from '../../images/exclamationred-circle.inline.svg'
import PersonImageSVG from '../../images/match.svg'
import NoDataSVG from '../../images/horizontal-dash.inline.svg'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import { getMatchColor } from '../../utils/colors'
import { MatchResults, MatchElementTypes } from '../../types/match'
import { getRequiredResult } from '../../utils/match'
import useMatchData from '../../hooks/useMatchData'
import Skeleton from '../Skeleton/Skeleton'
import clamp from 'lodash/clamp'
import { Match as MatchType } from 'components/Providers/MatchData'

interface MatchProps {
  sectionClass?: string
}

const Match: React.FC<MatchProps> = ({ sectionClass }) => {
  const { match, isLoading } = useMatchData()
  const { classes, cx } = useStyles({ match: match?.fit })
  const { defaultResumeProduct } = useSiteMetadata()
  const { t } = useTranslation()
  const tRef = 'matchCustomComponent'

  const getChipLabel = () => {
    switch (match?.fit) {
      case 'GREAT':
        return 'Top Match'

      case 'GOOD':
        return 'Good Match'

      case 'BAD':
        return 'Bad Match'

      default:
        return 'Ok Match'
    }
  }

  const capped = (param: string | null) => clamp(Number(param), 0, 8)

  const getProgressBarValue = (qualification: string | null) => {
    switch (qualification) {
      case MatchResults.great:
        return 100

      case MatchResults.good:
        return 80

      case MatchResults.ok:
        return 45

      case MatchResults.bad:
        return 20

      default:
        return 0
    }
  }

  const getReadabilityStatus = (pageCount: string | null) => {
    switch (pageCount) {
      case '':
        return 'No data'

      case '1':
        return 'Concise'

      case '2':
        return 'Perfect length'

      default:
        return 'Too long'
    }
  }

  const isGoodOrOk = (overallFit: string | null): boolean =>
    overallFit === MatchResults.good || overallFit === MatchResults.ok

  type statusKeyType =
    | 'skill_status'
    | 'experience_status'
    | 'info_status'
    | 'read_status'

  const sendMatchResult = (match: MatchType, key: statusKeyType) => {
    // If individual qualifications are falsy, we'd show the no data result OR
    // if they are either 'GOOD' or 'OK', they'd be preferred to the overall match score
    if (!match[key] || isGoodOrOk(match.fit)) {
      return match[key]
    }

    return match.fit
  }

  if (isLoading) {
    return (
      <Container className={cx(sectionClass, classes.container)}>
        {/* match overview box */}
        <Box className={classes.matchOverviewBox}>
          <img
            src={PersonImageSVG}
            className={classes.personImage}
            alt="A match analyst"
          />
          <Box
            className={cx(
              classes.matchResultsBox,
              classes.matchResultsBoxSkeleton
            )}
          >
            <Box className={classes.jobDetailsCardSkeleton}>
              <Skeleton
                components={[
                  {
                    classes: { root: classes.skeletonRoot, wave: classes.wave },
                    className: classes.chipSkeleton,
                  },
                  {
                    classes: { root: classes.skeletonRoot, wave: classes.wave },
                    className: classes.designationSkeleton,
                  },
                  {
                    classes: { root: classes.skeletonRoot, wave: classes.wave },
                    className: classes.recruiterSkeleton,
                  },
                ]}
              />
            </Box>
            <Skeleton
              components={[
                {
                  classes: { root: classes.skeletonRoot, wave: classes.wave },
                  className: classes.matchItemsSkeleton,
                },
              ]}
            />
          </Box>
        </Box>

        {/* match details box */}
        <Box>
          <Box className={classes.matchDetailsTitleSkeletonContainer}>
            <Skeleton
              components={Array(3).fill({
                classes: { root: classes.skeletonRoot, wave: classes.wave },
                width: '100%',
              })}
            />
          </Box>
          <Box className={classes.matchDetailsSubTitleSkeletonContainer}>
            <Skeleton
              components={Array(3).fill({
                classes: { root: classes.skeletonRoot, wave: classes.wave },
                width: '100%',
              })}
            />
          </Box>
          <Box className={classes.matchDetailsDescSkeletonContainer}>
            <Skeleton
              components={Array(2).fill({
                classes: { root: classes.skeletonRoot, wave: classes.wave },
                width: '100%',
              })}
            />
          </Box>
        </Box>
      </Container>
    )
  }

  if (match) {
    return (
      <Container className={cx(sectionClass, classes.container)}>
        {/* match overview box */}
        <Box className={classes.matchOverviewBox}>
          <img
            src={PersonImageSVG}
            className={classes.personImage}
            alt="A match analyst"
          />
          <Box className={classes.matchResultsBox}>
            <Card className={classes.jobDetailsCard}>
              <Chip
                label={getChipLabel()}
                size="small"
                className={classes.matchChip}
              />
              <CardHeader
                className={classes.jobDetails}
                disableTypography
                title={
                  <Typography
                    variant="body1"
                    className={cx(classes.semiBold, classes.noTransform)}
                  >
                    {match.job_title}
                  </Typography>
                }
                subheader={
                  <Typography
                    variant="h5"
                    className={cx(classes.jobLocation, classes.noTransform)}
                  >
                    {match.job_employer_name}
                  </Typography>
                }
              />
            </Card>
            <Divider />
            <Box className={classes.matchItemsContainer}>
              <MatchItem
                matchResult={sendMatchResult(match, 'skill_status')}
                matchItemTitle="Skills"
                matchItemResult={{
                  phrase: 'Found',
                  value: `${capped(match.skill_num_found)} ${
                    capped(match.skill_num_found) === 1 ? 'skill' : 'skills'
                  }`,
                }}
                progressBarValue={getProgressBarValue(match.skill_status)}
              />
              <MatchItem
                matchResult={sendMatchResult(match, 'experience_status')}
                matchItemTitle="Experience"
                matchItemResult={{
                  phrase: 'Found',
                  value: `${capped(match.experience_num_found)} ${
                    capped(match.experience_num_found) === 1
                      ? 'position'
                      : 'positions'
                  }`,
                }}
                progressBarValue={getProgressBarValue(match.experience_status)}
              />
              <MatchItem
                matchResult={sendMatchResult(match, 'info_status')}
                matchItemTitle="Key Info"
                matchItemResult={{
                  phrase: 'Found',
                  value: `${capped(match.info_num_found)} ${
                    capped(match.info_num_found) === 1 ? 'item' : 'items'
                  }`,
                }}
                progressBarValue={getProgressBarValue(match.info_status)}
              />
              <MatchItem
                matchResult={sendMatchResult(match, 'read_status')}
                matchItemTitle="Readability"
                matchItemResult={{
                  value: getReadabilityStatus(match.page_count),
                }}
                progressBarValue={getProgressBarValue(match.read_status)}
              />
            </Box>
          </Box>
        </Box>

        {/* match details box */}
        <Box>
          <Typography variant="h2">
            {getRequiredResult(
              match.fit,
              t(`${tRef}.titles.top`, { defaultResumeProduct }),
              t(`${tRef}.titles.ok`),
              t(`${tRef}.titles.bad`, { defaultResumeProduct })
            )}
          </Typography>
          <Typography className={classes.subTitle} variant="body1">
            {t(`${tRef}.description`, { defaultResumeProduct })}
          </Typography>
          <SkillCard
            matchResult={match.fit}
            title={`Missing ${capped(match.skill_num_missing)} ${
              Number(capped(match.skill_num_missing)) === 1 ? 'skill' : 'skills'
            }`}
            titleClass={classes.skillTitle}
            skills={match.skills_negative}
          />
          <SkillCard
            matchResult={match.fit}
            title={`Found ${capped(match.skill_num_found)} ${
              Number(capped(match.skill_num_found)) === 1 ? 'skill' : 'skills'
            }`}
            titleClass={classes.skillTitlePoistive}
            skills={match.skills_positive}
            ticked
          />
        </Box>
      </Container>
    )
  }

  return null
}

interface MatchItemProps {
  matchResult: string | null
  matchItemTitle: string | null
  matchItemResult: {
    phrase?: string | null
    value: string | null
  }
  progressBarValue: number
}

const MatchItem: React.FC<MatchItemProps> = ({
  matchResult,
  matchItemTitle,
  matchItemResult: { phrase = '', value },
  progressBarValue,
}) => {
  const { classes, cx } = useStyles({ match: matchResult })

  const getMatchItemSVG = () => {
    switch (matchResult) {
      case MatchResults.great:
      case MatchResults.good:
        return <TickGreenCicle />

      case MatchResults.ok:
        return <TickOrangeCicle />

      case MatchResults.bad:
        return <ExclamationRedCicle />

      default:
        return <NoDataSVG />
    }
  }

  const getPhrase = (): JSX.Element | 'No data' => {
    if (!matchResult) {
      return 'No data'
    }

    return (
      <>
        {phrase} <span className={classes.value}>{value}</span>
      </>
    )
  }

  return (
    <Box className={classes.matchItem}>
      <Box className={classes.matchItemTitle}>
        <Typography className={classes.noTransform} variant="h6">
          {matchItemTitle}
        </Typography>
        {getMatchItemSVG()}
      </Box>
      <Box className={classes.matchItemResult}>
        <Typography
          className={cx(classes.noTransform, !matchResult && classes.medium)}
          variant="h6"
        >
          {getPhrase()}
        </Typography>
        <LinearProgress
          classes={{
            determinate: !matchResult
              ? classes.barGreyedOut
              : classes.barDeterminate,
            bar1Determinate: classes.progressBar1,
          }}
          className={classes.progressBar}
          variant="determinate"
          value={progressBarValue}
        />
      </Box>
    </Box>
  )
}

interface SkillCardProps {
  matchResult: string | null
  title: string
  titleClass: string
  skills: string[]
  ticked?: boolean
}

const SkillCard: React.FC<SkillCardProps> = ({
  matchResult,
  title,
  titleClass,
  skills,
  ticked = false,
}) => {
  const { classes } = useStyles({ match: null })

  if (!skills) {
    return null
  }

  const getCrossSVG = () => {
    switch (matchResult) {
      case MatchResults.bad:
      case MatchResults.ok:
        return CrossRed
      default:
        return CrossOrange
    }
  }

  const renderSkillDetails = () => {
    let SVGToBeUsed = getCrossSVG()

    if (ticked) {
      SVGToBeUsed = TickGreen
    }

    const skillElements = []
    for (let i = 0; i < skills.length; i++) {
      if (i > 7) {
        break
      }

      skillElements.push(
        <Box className={classes.skillDetails}>
          <SVGToBeUsed /> {skills[i]}
        </Box>
      )
    }

    return skillElements
  }

  return (
    <Card className={classes.skillCard}>
      <CardHeader
        className={classes.skillCardTitle}
        disableTypography
        title={<Typography className={titleClass}>{title}</Typography>}
      />
      <CardContent className={classes.skillCardContent}>
        {renderSkillDetails()}
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles<{ match: any }>()((theme, { match }) => {
  const matchChipBgColor = getMatchColor(
    match,
    MatchElementTypes.chipBackground,
    theme
  )
  const matchChipTextColor = getMatchColor(
    match,
    MatchElementTypes.chipText,
    theme
  )

  const valueColor = getMatchColor(match, MatchElementTypes.resultValue, theme)

  const progressBar1BgColor = getMatchColor(
    match,
    MatchElementTypes.progressBar,
    theme
  )

  const skillTitleTextColor = getMatchColor(
    match,
    MatchElementTypes.skillTitle,
    theme
  )

  return {
    skeletonRoot: {
      '&&': { backgroundColor: theme.colors.neutral[10], borderRadius: '2px' },
    },
    wave: {
      '&&': {
        '&:after': {
          background: `linear-gradient(270.02deg, #EFF2F9 7.31%, ${theme.colors.white[100]} 19.28%, #EFF2F9 31.26%)`,
        },
      },
    },
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 0.5fr)',
      alignItems: 'center',
      paddingBlock: '4.5rem',
      gap: '6rem',
      maxWidth: '100%',

      [theme.breakpoints.down('lg')]: {
        margin: '0 auto',
        gridTemplateColumns: '1fr',
        gap: '4rem',
        maxWidth: '62%',
      },

      [theme.breakpoints.down('md')]: {
        gap: '2rem',
        maxWidth: '78%',
        paddingBlock: '4rem',
      },

      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    matchOverviewBox: {
      display: 'flex',
      flexDirection: 'column',
    },
    personImage: {
      maxWidth: '40.8784%',
      alignSelf: 'flex-end',
      marginRight: '4%',
    },
    matchResultsBoxSkeleton: {
      '&&': {
        gap: '1.875rem',
        gridTemplateRows: 'repeat(2, min-content)',
      },
    },
    matchResultsBox: {
      padding: '1.5rem',
      display: 'grid',
      gap: '1rem',
      gridTemplateRows: 'repeat(3, min-content)',
      borderRadius: '16px',
      boxShadow:
        '0px 2px 4px -1px rgba(15, 56, 113, 0.08), 0px 8px 20px -4px rgba(15, 56, 113, 0.12)',
      marginTop: '-16%',

      [theme.breakpoints.down('sm')]: {
        gap: '0.5rem',
        padding: '0.5rem',
      },
    },
    jobDetailsCardSkeleton: {
      display: 'grid',
      gap: '0.5rem',
    },
    jobDetailsCard: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      boxShadow: 'none',
      padding: 0,
    },
    chipSkeleton: {
      paddingBlock: '1.5%',
      width: '15%',
    },
    designationSkeleton: {
      paddingBlock: '1.5%',
      width: '45%',
    },
    recruiterSkeleton: {
      paddingBlock: '1.5%',
      width: '35%',
    },
    matchChip: {
      backgroundColor: matchChipBgColor,
      color: matchChipTextColor,
      fontSize: '14px',
      fontWeight: 600,
    },
    jobDetails: {
      marginTop: '0.5rem',
      padding: 0,
      maxWidth: '55%',
    },
    semiBold: {
      fontWeight: 600,
    },
    medium: {
      fontWeight: 500,
    },
    noTransform: {
      textTransform: 'none',
    },
    jobLocation: {
      fontWeight: 400,
    },
    matchItemsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '0.5rem',
    },
    matchItemsSkeleton: {
      paddingBlock: '30%',
    },
    matchItem: {
      padding: '0.75rem',
      border: `1px solid ${theme.colors.neutral[15]}`,
      borderRadius: '8px',

      [theme.breakpoints.down('sm')]: {
        padding: '0.5rem',
      },
    },
    matchItemTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    matchItemResult: {
      marginTop: '0.75rem',
    },
    value: {
      color: valueColor,
    },
    progressBar: {
      marginTop: '0.5rem',
    },
    progressBar1: {
      backgroundColor: progressBar1BgColor,
    },
    barDeterminate: {
      backgroundColor: theme.colors.neutral[15],
      borderRadius: '4px',
    },
    barGreyedOut: {
      backgroundColor: theme.colors.neutral[30],
    },
    matchDetailsTitleSkeletonContainer: {
      display: 'grid',
      gap: '0.5rem',

      '& > *': {
        paddingBlock: '2.76%',
      },

      '& > *:last-child': {
        [theme.breakpoints.up('sm')]: {
          display: 'none',
        },
      },
    },
    matchDetailsSubTitleSkeletonContainer: {
      display: 'grid',
      gap: '0.5rem',
      marginTop: '1rem',

      '& > *': {
        paddingBlock: '2.1%',
      },

      '& > *:last-child': {
        [theme.breakpoints.up('sm')]: {
          display: 'none',
        },
      },
    },
    matchDetailsDescSkeletonContainer: {
      display: 'grid',
      gap: '1.5rem',
      marginTop: '1.5rem',

      '& > *': {
        paddingBlock: '10.5%',
      },
    },
    subTitle: {
      marginTop: '0.375rem',
    },
    skillCard: {
      marginTop: '1.5rem',
      borderRadius: 0,
      padding: 0,
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    skillCardTitle: {
      padding: 0,
    },
    skillTitle: {
      fontWeight: 600,
      color: skillTitleTextColor,
    },
    skillTitlePoistive: {
      fontWeight: 600,
      color: theme.colors.green[40],
    },
    skillCardContent: {
      '&&': {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 0.5fr)',
        marginTop: '0.75rem',
        backgroundColor: '#f7f9fc',
        padding: '0.75rem',
        rowGap: '0.5rem',

        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr',
        },
      },
    },
    skillDetails: {
      display: 'flex',
      borderRadius: '8px',
      alignItems: 'center',
    },
  }
})

export default Match
